/**
 * The dev auth shim, and the three fences that keep it from escaping.
 *
 * `AUTH_MODE=dev` trusts an `X-Dev-User` header without verification. That is
 * fine on a laptop against the sandbox and catastrophic on
 * `inventory.cubanbread.com`, so the shim is fenced three ways (phase3 doc § 6.4)
 * and each fence is independent:
 *
 * **1. Build-time, not runtime.** `DEV_SHIM_COMPILED_IN` reads
 * `import.meta.env.VITE_AUTH_MODE`, which Vite replaces with a literal at build
 * time. A production build (`VITE_AUTH_MODE` unset) therefore folds it to `false`
 * and the bundler drops the header code and the UPN form entirely. Not an
 * environment sniff, not a runtime toggle — **absent from the bytes**.
 *
 * **2. The server's answer wins.** Even in a dev-mode bundle, `X-Dev-User` is
 * only sent when `GET /auth/config` reports `auth_mode: "dev"`. Point a dev build
 * at a real `entra` backend and it sends no shim header at all. Cheap belt and
 * braces, and the reason `authMode` is threaded into the client rather than read
 * from the bundle.
 *
 * **3. It is impossible to miss.** While the shim is live a permanent banner sits
 * on every screen, so a screenshot taken in dev mode is obviously a dev-mode
 * screenshot.
 */

/**
 * Whether this bundle contains the shim at all. A build-time literal.
 *
 * Set by `VITE_AUTH_MODE=dev npm run dev`. Deliberately compared to the exact
 * string so any other value — including `entra`, `prod`, or a typo — yields a
 * production bundle with no shim in it. Fail closed.
 */
export const DEV_SHIM_COMPILED_IN = import.meta.env.VITE_AUTH_MODE === "dev";

export const DEV_USER_HEADER = "X-Dev-User";

/**
 * Where the pretend identity lives: `sessionStorage`, not `localStorage`.
 *
 * A dev identity that survives closing the tab would start behaving like a real
 * session, which is the one impression this shim must never give.
 */
const STORAGE_KEY = "lasegunda.devUpn";

export function readDevUpn(): string | null {
  if (!DEV_SHIM_COMPILED_IN) return null;
  try {
    return window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    // Private-mode iPads throw on storage access. The shim is a convenience;
    // losing it is not worth taking the app down.
    return null;
  }
}

export function writeDevUpn(upn: string | null): void {
  if (!DEV_SHIM_COMPILED_IN) return;
  try {
    if (upn) window.sessionStorage.setItem(STORAGE_KEY, upn);
    else window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* see readDevUpn */
  }
}

/**
 * Whether to actually send the shim header.
 *
 * Both fences in one expression, which is why callers ask this rather than
 * checking `DEV_SHIM_COMPILED_IN` themselves: the bundle must contain the shim
 * **and** the server must have said it is in dev mode. `authMode` is `null` while
 * `/auth/config` is still in flight — treated as "not dev", so a slow config read
 * can never leak the header.
 */
export function shouldSendDevHeader(authMode: string | null): boolean {
  return DEV_SHIM_COMPILED_IN && authMode === "dev";
}
